import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, AlertCircle, FileText, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { EligibilitySchema, EligibilityCriterion, EligibilityField } from '@/components/settings/EligibilitySchemaEditor';
import type { Json } from '@/integrations/supabase/types';

interface ItemEligibilityFormProps {
  itemId: string;
  catalogId: string;
  currentEligibility?: Record<string, any>;
  onSave?: () => void;
}

interface CriterionMet {
  key: string;
  evidence: string;
}

interface EligibilityData {
  criteria_met?: CriterionMet[];
  narrative?: string;
  evidence_collected?: string[];
  [key: string]: any;
}

export default function ItemEligibilityForm({ itemId, catalogId, currentEligibility, onSave }: ItemEligibilityFormProps) {
  const { t } = useTranslation();
  const [schema, setSchema] = useState<EligibilitySchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<EligibilityData>({});

  // Carregar schema do catálogo
  useEffect(() => {
    const loadSchema = async () => {
      const { data, error } = await supabase
        .from('catalog')
        .select('eligibility_schema')
        .eq('id', catalogId)
        .single();

      if (error) {
        console.error('Error loading schema:', error);
        setLoading(false);
        return;
      }

      setSchema(data?.eligibility_schema as unknown as EligibilitySchema);
      setLoading(false);
    };

    loadSchema();
  }, [catalogId]);

  // Inicializar formData com valores existentes
  useEffect(() => {
    if (currentEligibility) {
      setFormData(currentEligibility);
    }
  }, [currentEligibility]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('items')
        .update({ eligibility: formData as unknown as Json })
        .eq('id', itemId);

      if (error) throw error;

      toast.success('Elegibilidade salva com sucesso');
      onSave?.();
    } catch (error) {
      console.error('Error saving eligibility:', error);
      toast.error('Erro ao salvar elegibilidade');
    } finally {
      setSaving(false);
    }
  };

  // Handlers para critérios (Merit)
  const handleCriterionToggle = (criterion: EligibilityCriterion, checked: boolean) => {
    const current = formData.criteria_met || [];
    if (checked) {
      setFormData({
        ...formData,
        criteria_met: [...current, { key: criterion.key, evidence: '' }],
      });
    } else {
      setFormData({
        ...formData,
        criteria_met: current.filter(c => c.key !== criterion.key),
      });
    }
  };

  const handleCriterionEvidence = (key: string, evidence: string) => {
    const current = formData.criteria_met || [];
    const updated = current.map(c => 
      c.key === key ? { ...c, evidence } : c
    );
    setFormData({ ...formData, criteria_met: updated });
  };

  const isCriterionMet = (key: string) => {
    return (formData.criteria_met || []).some(c => c.key === key);
  };

  const getCriterionEvidence = (key: string) => {
    return (formData.criteria_met || []).find(c => c.key === key)?.evidence || '';
  };

  // Handlers para evidências (Humanitarian)
  const handleEvidenceToggle = (key: string, checked: boolean) => {
    const current = formData.evidence_collected || [];
    if (checked) {
      setFormData({
        ...formData,
        evidence_collected: [...current, key],
      });
    } else {
      setFormData({
        ...formData,
        evidence_collected: current.filter(e => e !== key),
      });
    }
  };

  // Handler para campos adicionais
  const handleFieldChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  // Validação
  const getValidation = () => {
    if (!schema) return { valid: true, issues: [] };

    const issues: string[] = [];

    if (schema.type === 'merit' && schema.criteria && schema.min_criteria) {
      const metCount = (formData.criteria_met || []).length;
      if (metCount < schema.min_criteria) {
        issues.push(`Mínimo de ${schema.min_criteria} critérios (${metCount} selecionados)`);
      }
    }

    if (schema.narrative_required && !formData.narrative?.trim()) {
      issues.push('Narrativa obrigatória');
    }

    schema.additional_fields?.forEach(field => {
      if (field.required && !formData[field.key]) {
        issues.push(`${field.label} é obrigatório`);
      }
    });

    return { valid: issues.length === 0, issues };
  };

  const validation = getValidation();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!schema) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Este tipo de serviço não requer elegibilidade</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Elegibilidade
            <Badge variant="outline">{schema.category || schema.type}</Badge>
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {validation.valid ? (
              <Badge variant="default" className="bg-green-500">
                <CheckCircle className="h-3 w-3 mr-1" />
                Completo
              </Badge>
            ) : (
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                Incompleto
              </Badge>
            )}
            
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Salvar
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Validation Issues */}
        {!validation.valid && (
          <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            <p className="font-medium mb-1">Pendências:</p>
            <ul className="list-disc list-inside">
              {validation.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Merit Criteria */}
        {schema.type === 'merit' && schema.criteria && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                Critérios Atendidos
              </Label>
              <span className="text-sm text-muted-foreground">
                {(formData.criteria_met || []).length} / {schema.min_criteria} mínimo
              </span>
            </div>
            
            <div className="space-y-3">
              {schema.criteria.map((criterion) => (
                <div key={criterion.key} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isCriterionMet(criterion.key)}
                      onCheckedChange={(checked) => 
                        handleCriterionToggle(criterion, checked as boolean)
                      }
                    />
                    <span className={isCriterionMet(criterion.key) ? 'font-medium' : ''}>
                      {criterion.label}
                    </span>
                  </div>
                  
                  {isCriterionMet(criterion.key) && (
                    <Textarea
                      value={getCriterionEvidence(criterion.key)}
                      onChange={(e) => handleCriterionEvidence(criterion.key, e.target.value)}
                      placeholder="Descreva a evidência para este critério..."
                      className="ml-7"
                      rows={2}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Humanitarian Narrative */}
        {schema.type !== 'merit' && schema.narrative_required && (
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              {schema.narrative_label || 'Narrativa'}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Textarea
              value={formData.narrative || ''}
              onChange={(e) => handleFieldChange('narrative', e.target.value)}
              placeholder="Descreva a situação do cliente..."
              rows={5}
            />
          </div>
        )}

        {/* Evidence Types (Humanitarian) */}
        {schema.type !== 'merit' && schema.evidence_types && schema.evidence_types.length > 0 && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              Evidências Coletadas
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {schema.evidence_types.map((evidence) => (
                <div key={evidence.key} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                  <Checkbox
                    checked={(formData.evidence_collected || []).includes(evidence.key)}
                    onCheckedChange={(checked) => 
                      handleEvidenceToggle(evidence.key, checked as boolean)
                    }
                  />
                  <span className="text-sm">{evidence.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Fields */}
        {schema.additional_fields && schema.additional_fields.length > 0 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">
              Informações Adicionais
            </Label>
            
            <div className="grid grid-cols-2 gap-4">
              {schema.additional_fields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  
                  {field.type === 'text' && (
                    <Input
                      value={formData[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    />
                  )}
                  
                  {field.type === 'number' && (
                    <Input
                      type="number"
                      value={formData[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, parseFloat(e.target.value) || '')}
                    />
                  )}
                  
                  {field.type === 'boolean' && (
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={formData[field.key] || false}
                        onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
                      />
                      <span className="text-sm">Sim</span>
                    </div>
                  )}
                  
                  {field.type === 'date' && (
                    <Input
                      type="date"
                      value={formData[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    />
                  )}
                  
                  {field.type === 'select' && field.options && (
                    <Select
                      value={formData[field.key] || ''}
                      onValueChange={(value) => handleFieldChange(field.key, value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
